export const DEFAULT_CAMPAIGN_SORT_BY = "updated_at" as const;
export const DEFAULT_CAMPAIGN_SORT_ORDER = "desc" as const;

export type CampaignListSortBy =
  | "updated_at"
  | "starts_at"
  | "ends_at"
  | "status"
  | "category"
  | "reached_users"
  | "used_users"
  | "conversion_rate";

export type CampaignListSortOrder = "asc" | "desc";

/** Descend → ascend → clear. Matches DataTable's descend-first click, plus reset. */
export const CAMPAIGN_COLUMN_SORT_DIRECTIONS: Array<"descend" | "ascend" | null> = [
  "descend",
  "ascend",
  null,
];

const FIRST_COLUMN_SORT_ORDER: CampaignListSortOrder = "desc";
const SECOND_COLUMN_SORT_ORDER: CampaignListSortOrder = "asc";

const COLUMN_TO_SORT_BY = {
  status: "status",
  rowType: "category",
  reached_users: "reached_users",
  used_users: "used_users",
  conversion_rate: "conversion_rate",
  starts_at: "starts_at",
  ends_at: "ends_at",
} as const satisfies Record<string, CampaignListSortBy>;

type SortableColumnKey = keyof typeof COLUMN_TO_SORT_BY;

const SORT_BY_TO_COLUMN: Record<CampaignListSortBy, string | null> = {
  updated_at: null,
  status: "status",
  category: "rowType",
  reached_users: "reached_users",
  used_users: "used_users",
  conversion_rate: "conversion_rate",
  starts_at: "starts_at",
  ends_at: "ends_at",
};

export const sortByForColumn = (
  columnKey: string,
): CampaignListSortBy | undefined => {
  if (!Object.prototype.hasOwnProperty.call(COLUMN_TO_SORT_BY, columnKey)) {
    return undefined;
  }
  return COLUMN_TO_SORT_BY[columnKey as SortableColumnKey];
};

export const isDefaultCampaignSort = (
  sortBy: CampaignListSortBy,
  sortOrder: CampaignListSortOrder,
): boolean =>
  sortBy === DEFAULT_CAMPAIGN_SORT_BY && sortOrder === DEFAULT_CAMPAIGN_SORT_ORDER;

export const serverSortColumnProps = (
  columnKey: string,
  sortBy: CampaignListSortBy,
  sortOrder: CampaignListSortOrder,
): {
  sorter: true;
  sortOrder: "ascend" | "descend" | null;
  sortDirections: Array<"descend" | "ascend" | null>;
  showSorterTooltip: false;
} | Record<string, never> => {
  if (sortByForColumn(columnKey) === undefined) {
    return {};
  }
  const activeColumn = SORT_BY_TO_COLUMN[sortBy];
  return {
    sorter: true,
    sortOrder:
      activeColumn === columnKey
        ? sortOrder === "asc"
          ? "ascend"
          : "descend"
        : null,
    sortDirections: CAMPAIGN_COLUMN_SORT_DIRECTIONS,
    showSorterTooltip: false,
  };
};

type NormalizedSorter = {
  field: string | null;
  order: "ascend" | "descend" | null;
};

export const normalizeCampaignSorter = (sorter: unknown): NormalizedSorter => {
  const normalized = (Array.isArray(sorter) ? sorter[0] : sorter) as
    | {
        field?: unknown;
        columnKey?: unknown;
        order?: unknown;
      }
    | undefined;
  const field = normalized?.columnKey ?? normalized?.field;
  const order = normalized?.order;
  return {
    field: field ? String(field) : null,
    order: order === "ascend" || order === "descend" ? order : null,
  };
};

export type CampaignSortChange =
  | { type: "noop" }
  | {
      type: "update";
      sortBy: CampaignListSortBy;
      sortOrder: CampaignListSortOrder;
    };

export const resolveCampaignSortChange = ({
  sorter,
  currentSortBy,
  currentSortOrder,
}: {
  sorter: unknown;
  currentSortBy: CampaignListSortBy;
  currentSortOrder: CampaignListSortOrder;
}): CampaignSortChange => {
  const activeSorter = normalizeCampaignSorter(sorter);
  const clickedSortBy = activeSorter.field
    ? sortByForColumn(activeSorter.field)
    : undefined;
  const reset = {
    type: "update" as const,
    sortBy: DEFAULT_CAMPAIGN_SORT_BY,
    sortOrder: DEFAULT_CAMPAIGN_SORT_ORDER,
  };

  // Ant Design strips columnKey/order when the cycle lands on cancel
  // (`generateSorterInfo` returns an empty sorter). Treat that as reset.
  if (!clickedSortBy) {
    if (isDefaultCampaignSort(currentSortBy, currentSortOrder)) {
      return { type: "noop" };
    }
    return reset;
  }

  const columnIsActive =
    clickedSortBy === currentSortBy &&
    !isDefaultCampaignSort(currentSortBy, currentSortOrder);

  if (!columnIsActive) {
    return {
      type: "update",
      sortBy: clickedSortBy,
      sortOrder: FIRST_COLUMN_SORT_ORDER,
    };
  }
  if (currentSortOrder === FIRST_COLUMN_SORT_ORDER) {
    return {
      type: "update",
      sortBy: clickedSortBy,
      sortOrder: SECOND_COLUMN_SORT_ORDER,
    };
  }
  return reset;
};
